import React, { FunctionComponent, useState } from 'react';
import DashboardLayout from '../../components/layout/layout';
import { IconButton,Alert, Box, Button, Grid,Paper, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useUploadInventory } from '../../hooks/api/user';
import MyDownloadIcon from '../../assets/img/Download (19).png';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ActionCard = styled(Paper)(({ theme }) => ({
  width: 180,
  height: 100,
  backgroundColor: '#f2f2f2',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '2px 2px 4px rgba(0,0,0,0.2)',
  borderRadius: 4,
  cursor: 'pointer',
  textAlign: 'center'
}));

interface UnmappedPart {
  RepNum: string;
  QTY: string;
}

interface ValidPart {
  BRK_NO: string;
  QTY: string;
}

interface UploadResponse {
  message: string;
  unmappedParts: UnmappedPart[];
  validParts: ValidPart[];
  recordsProcessed: number;
}
interface NormalizedPart {
  partNumber: string;
  qty: string;
  type: 'Inventory Updated' | 'Needs Mapping';
}

const InventoryUpload: FunctionComponent = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);

  const [uploadError, setUploadError] = useState<string>('');
  const [uploadSuccess, setUploadSuccess] = useState<boolean>();
  const [ uploadDisabled, setUploadDisabled ] = useState<boolean>(false);
  
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(null);
  const [combinedParts, setCombinedParts] = useState<NormalizedPart[]>([]);
  const { mutateAsync: uploadFile } = useUploadInventory();

  const resetFileInput = () => {
    const fileInput = document.getElementById('upload-file') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    setFile(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError('');
    setUploadSuccess(false);
    setUploadResponse(null);         // <- clear old response
    setCombinedParts([]);            // <- clear old table data
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    } else {
      setFile(null);
    }
  }; 
  
  const handleUploadAction = async () => {
    if (!file) {
      setUploadError('Please select a file to upload.');
      setUploadSuccess(false);
      return;
    }
    const allowedExtensions = ['.txt', '.csv', '.xlsx'];
    const fileName = file.name.toLowerCase();
    const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isValidExtension) {
      setUploadError('Invalid file type. Only .txt, .csv, or .xlsx files are allowed.');
      setUploadSuccess(false);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadDisabled(true);
      const result = await uploadFile(formData);
      if (result?.status === 200) {
        setUploadSuccess(true);
        const data = result.data as UploadResponse;

        const normalizedParts: NormalizedPart[] = [
          ...data.validParts.map((part) => ({
            partNumber: part.BRK_NO.trim(),
            qty: part.QTY,
            type: 'Inventory Updated' as const,
          })),
          // Skip unmapped parts from UI display as requested by client
          // ...data.unmappedParts.map((part) => ({
          //   partNumber: part.RepNum,
          //   qty: part.QTY,
          //   type: 'Needs Mapping' as const,
          // })),
        ];

        setCombinedParts(normalizedParts);

        setUploadResponse(data);

        setTimeout(() => {
          setUploadSuccess(false);
          resetFileInput();
          setUploadDisabled(false);
        }, 3000);
      }else {
        setUploadDisabled(false);
        setUploadError(result?.data?.message || 'An error occurred during login. Please try again.');
        //setUploadError('An error occurred during login. Please try again.');
        setTimeout(() => {
          setUploadError('');
        }, 3000);
      }
    } catch (err: any) {
      setUploadDisabled(false);
      const message = err.response?.data?.message || err.message || 'Unexpected error';
      setUploadError(message);
    }
  }

  const handleDownloadPDF = () => {
    const doc = new jsPDF();

    doc.text("Parts Summary", 14, 15);

    // Only include valid parts (skip unmapped parts) as requested by client
    const validPartsOnly = combinedParts.filter(part => part.type === 'Inventory Updated');
    autoTable(doc, {
      startY: 20,
      head: [['Part Number', 'Quantity', 'Type']],
      body: validPartsOnly.map(part => [part.partNumber, part.qty, part.type]),
    });

    doc.save("parts-summary.pdf");
  };

  
  return (
    <>
      <DashboardLayout titleIcon={<LockOutlinedIcon />} title={"Inventory"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={''} breadcrumbs={' Inventory Upload'} activeMenu={'uploadInventory'}>
        <Box sx={{ width: '100%', maxWidth: '100%' }}>
          <Grid>
            <ActionCard sx={{ width: '100%', minHeight: '120px', height: 'auto',  display: 'block', textAlign: 'left', p: '10px 15px', border: '1px solid #e1e1e1', boxShadow: ` inset 0 0 10px rgba(0, 0, 0, 0.1), inset 0 0 5px rgba(255, 255, 255, 0.6)` }}>
              <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
                Upload Inventory
              </Typography>

              <label htmlFor="upload-file">
                <Button disabled={uploadDisabled} component="label" variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', marginRight: '25px' }}>
                  Choose File
                  <input
                    id="upload-file"
                    type="file"
                    accept="*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </Button>
              </label>
              <Button disabled={uploadDisabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} onClick={handleUploadAction}>
                Upload File
              </Button>
              {file && (
                <Button disabled={uploadDisabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', marginLeft: '25px' }} onClick={resetFileInput}>
                  Clear
                </Button>
              )}
              {file && (
                <Typography variant="body2" mt={1}>
                  Selected: {file.name}
                </Typography>
              )}
              <Grid size={12}>
                {uploadError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {uploadError}
                    
                  </Alert>
                )}
                {/* {
                  uploadSuccess && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      File uploaded successfully!
                    </Alert>
                  )
                } */}
              </Grid>
            </ActionCard>
          </Grid>

            {uploadResponse && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {uploadResponse.message} (Total Records Processed: {uploadResponse.recordsProcessed})
              </Alert>
              )}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
                Parts Summary
              </Typography>
              <IconButton size="small" aria-label="download" onClick={handleDownloadPDF}>
                <img
                  src={MyDownloadIcon}
                  alt="Download"
                  style={{ width: 35, height: 35}}
                />
              </IconButton>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0, mt: 3 }}>
              <Table size="small" aria-label="Combined parts table">
                <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                  <TableRow>
                    <TableCell><strong>Part Number</strong></TableCell>
                    <TableCell><strong>Quantity</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {combinedParts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No parts data yet. Upload a file to see results.
                      </TableCell>
                    </TableRow>
                  ) : (
                    combinedParts.map((part: NormalizedPart, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{part.partNumber}</TableCell>
                        <TableCell>{part.qty}</TableCell>
                        <TableCell>{part.type}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 3, textAlign: 'right' }}>
              <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} onClick={() => navigate(-1)}>
                BACK
              </Button>
            </Box>  
          </Box>
        </Box>
      </DashboardLayout>  
    </>
  )
};

export default InventoryUpload;